import { useState, useEffect, useMemo } from 'react';
import Head from 'next/head';
import Layout from '@/components/Layout';
import { Documento, Cliente } from '@/types';
import { getDocumentos, getClientes } from '@/lib/supabaseService';
import { useAuth } from '@/lib/authContext';
import FilterBar, { Filter } from '@/components/FilterBar';
import DocumentViewer from '@/components/DocumentViewer';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/* ─── Icon helper ─── */
const Icon = ({ name, className }: { name: string; className?: string }) => (
  <span className={cn('material-symbols-outlined', className)}>{name}</span>
);

/* ─── Status / Confidence configuration ─── */

const ESTADO_INFO: Record<string, { label: string; dot: string; badge: string }> = {
  en_proceso:   { label: 'En proceso',   dot: 'bg-blue-500',    badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  procesado_ia: { label: 'Procesado IA', dot: 'bg-amber-400',   badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  validado:     { label: 'Validado',     dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const CONFIDENCE_PILLS: Array<{ key: string | null; label: string }> = [
  { key: 'alta',  label: 'Alta confianza' },
  { key: 'media', label: 'Media confianza' },
  { key: 'baja',  label: 'Baja confianza' },
  { key: null,     label: 'Todos' },
];

const getEstadoInfo = (estado: string) =>
  ESTADO_INFO[estado] || { label: estado || 'Desconocido', dot: 'bg-slate-300', badge: 'bg-slate-50 text-calm-muted border-calm-border' };

const getConfidenceColor = (confianza: number) => {
  if (confianza >= 95) return { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800' };
  if (confianza >= 80) return { bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200', dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800' };
  return { bg: 'bg-red-50', text: 'text-red-700', ring: 'ring-red-200', dot: 'bg-red-500', badge: 'bg-red-100 text-red-800' };
};

export default function AsesorContabilizacionIA() {
  const { user } = useAuth();
  const [allClientes, setAllClientes] = useState<Cliente[]>([]);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState<Record<string, any>>({});
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState<Documento | null>(null);
  const [mostrarVisor, setMostrarVisor] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confidenceFilter, setConfidenceFilter] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      try {
        const [docs, cls] = await Promise.all([
          getDocumentos(undefined, user.id),
          getClientes(user.id),
        ]);
        setAllClientes(cls);
        const iaFiltered = docs.filter(d =>
          d.procesadoIA && (d.estado === 'procesado_ia' || d.estado === 'validado')
        );
        setDocumentos(iaFiltered);
      } catch (e) {
        console.error('Error loading IA docs:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  const findCliente = (clienteId: string) => allClientes.find(c => c.id === clienteId);
  const clientesAsignados = allClientes;

  const filterConfig: Filter[] = [
    {
      key: 'buscar',
      label: 'Buscar',
      type: 'text',
      placeholder: 'Nombre documento, cliente...',
    },
    {
      key: 'cliente',
      label: 'Cliente',
      type: 'select',
      options: clientesAsignados.map(c => ({ value: c.id, label: c.razonSocial })),
    },
  ];

  /* ── Filtering pipeline ── */
  const documentosFiltradosBase = useMemo(() => documentos.filter(doc => {
    if (filtros.buscar) {
      const busqueda = filtros.buscar.toLowerCase();
      const cliente = findCliente(doc.clienteId);
      if (!doc.nombre.toLowerCase().includes(busqueda) && 
          !cliente?.razonSocial.toLowerCase().includes(busqueda)) {
        return false;
      }
    }
    if (filtros.cliente && doc.clienteId !== filtros.cliente) return false;
    return true;
  }), [documentos, filtros, allClientes]);

  /* Confidence counts (computed on base-filtered docs) */
  const confidenceCounts = useMemo(() => {
    const counts = { alta: 0, media: 0, baja: 0, todos: documentosFiltradosBase.length };
    documentosFiltradosBase.forEach(d => {
      const c = d.datosIA?.nivelConfianza ?? 0;
      if (c >= 95) counts.alta++;
      else if (c >= 80) counts.media++;
      else counts.baja++;
    });
    return counts;
  }, [documentosFiltradosBase]);

  /* Final filtered list (base + confidence pill) */
  const documentosFiltrados = useMemo(() => {
    if (!confidenceFilter) return documentosFiltradosBase;
    return documentosFiltradosBase.filter(doc => {
      const c = doc.datosIA?.nivelConfianza ?? 0;
      if (confidenceFilter === 'alta') return c >= 95;
      if (confidenceFilter === 'media') return c >= 80 && c < 95;
      if (confidenceFilter === 'baja') return c < 80;
      return true;
    });
  }, [documentosFiltradosBase, confidenceFilter]);

  const handleFiltroChange = (key: string, value: any) => {
    setFiltros({ ...filtros, [key]: value });
  };

  const handleLimpiarFiltros = () => {
    setFiltros({});
  };

  const handleVerDocumento = (doc: Documento) => {
    setDocumentoSeleccionado(doc);
    setMostrarVisor(true);
  };

  const handleValidar = (doc: Documento) => {
    toast.success(`Asiento contable validado y listo para enviar a ERP: ${doc.nombre}`);
  };

  const handleGenerarLibroRegistro = () => {
    toast.success('Generando Libro Registro automático para documentos validados...');
  };

  /* ── Selection handlers ── */
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const ids = documentosFiltrados.map(d => d.id);
    const allSelected = ids.length > 0 && ids.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(ids));
  };

  const allTableSelected = documentosFiltrados.length > 0 && documentosFiltrados.every(d => selectedIds.has(d.id));

  /* ── Bulk actions ── */
  const handleValidarTodos = () => {
    if (selectedIds.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }
    const count = Array.from(selectedIds).filter(id => {
      const doc = documentos.find(d => d.id === id);
      return doc?.estado === 'procesado_ia';
    }).length;
    toast.success(`${count} documento(s) validado(s) y listos para enviar a ERP`);
    setSelectedIds(new Set());
  };

  const handleRechazarSeleccionados = () => {
    if (selectedIds.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }
    toast.success(`${selectedIds.size} documento(s) rechazado(s)`);
    setSelectedIds(new Set());
  };

  /* ── CSV Export ── */
  const handleExportCSV = () => {
    if (documentosFiltrados.length === 0) {
      toast.error('No hay documentos para exportar');
      return;
    }

    const headers = [
      'Documento', 'Cliente', 'Confianza (%)', 'Estado',
      'Nº Factura', 'Proveedor', 'Importe Total', 'Fecha',
    ];

    const rows = documentosFiltrados.map(doc => {
      const cliente = findCliente(doc.clienteId);
      const confianza = doc.datosIA?.nivelConfianza ?? 0;
      const importe = doc.datosIA?.importeTotal;
      return [
        `"${doc.nombre.replace(/"/g, '""')}"`,
        `"${(cliente?.razonSocial || '').replace(/"/g, '""')}"`,
        confianza.toFixed(0),
        `"${getEstadoInfo(doc.estado).label}"`,
        `"${doc.datosIA?.numeroFactura || ''}"`,
        `"${(doc.datosIA?.proveedor || '').replace(/"/g, '""')}"`,
        importe !== undefined ? Number(importe).toFixed(2) : '',
        `"${doc.datosIA?.fecha || ''}"`,
      ].join(';');
    });

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contabilizacion-ia-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Archivo CSV exportado correctamente');
  };

  const estadisticas = {
    total: documentos.length,
    altaConfianza: documentos.filter(d => d.datosIA && d.datosIA.nivelConfianza >= 95).length,
    mediaConfianza: documentos.filter(d => d.datosIA && d.datosIA.nivelConfianza >= 80 && d.datosIA.nivelConfianza < 95).length,
    bajaConfianza: documentos.filter(d => d.datosIA && d.datosIA.nivelConfianza < 80).length,
    validados: documentos.filter(d => d.estado === 'validado').length,
  };

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <>
        <Head>
          <title>Contabilización IA - Externaliza2</title>
        </Head>
        <Layout rol="asesor">
          <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pb-12 space-y-12 bg-calm-bg">
            <div className="pt-12">
              <Skeleton className="h-6 w-40 rounded-huge mb-2" />
              <Skeleton className="h-12 w-96 rounded-huge" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-28 rounded-huge" />
              ))}
            </div>
            <div className="flex gap-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-40 rounded-huge" />
              ))}
            </div>
            <Skeleton className="h-96 rounded-huge" />
          </div>
        </Layout>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>Contabilización IA - Externaliza2</title>
      </Head>
      <Layout rol="asesor">
        <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pb-12 space-y-12 bg-calm-bg">

          {/* ── Header ── */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pt-12">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-calm-accent mb-2">Inteligencia Artificial</p>
              <h1 className="font-outfit text-5xl font-light text-calm-text tracking-tight">Contabilización IA</h1>
              <p className="text-sm font-light text-calm-muted mt-2">Documentos procesados por inteligencia artificial</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleExportCSV}
                className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:shadow-sm transition-all flex items-center gap-2"
              >
                <Icon name="table_chart" className="text-[18px]" />
                Exportar Excel
              </button>
              <button
                onClick={handleGenerarLibroRegistro}
                className="bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors flex items-center gap-2"
              >
                <Icon name="auto_awesome" className="text-[18px]" />
                Generar Libro Registro
              </button>
            </div>
          </div>

          {/* ── Stats Summary Cards ── */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
              <p className="text-[10px] uppercase tracking-widest text-calm-muted">Total procesados</p>
              <p className="text-2xl font-light text-calm-text mt-2 tabular-nums">{estadisticas.total}</p>
            </div>
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <p className="text-[10px] uppercase tracking-widest text-calm-muted">Alta confianza</p>
              </div>
              <p className="text-2xl font-light text-emerald-600 mt-2 tabular-nums">{estadisticas.altaConfianza}</p>
            </div>
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <p className="text-[10px] uppercase tracking-widest text-calm-muted">Media confianza</p>
              </div>
              <p className="text-2xl font-light text-amber-600 mt-2 tabular-nums">{estadisticas.mediaConfianza}</p>
            </div>
            <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <p className="text-[10px] uppercase tracking-widest text-calm-muted">Baja confianza</p>
              </div>
              <p className="text-2xl font-light text-red-600 mt-2 tabular-nums">{estadisticas.bajaConfianza}</p>
            </div>
            <div className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
              <div className="flex items-center gap-1.5">
                <Icon name="check_circle" className="text-[14px] text-emerald-500" />
                <p className="text-[10px] uppercase tracking-widest text-calm-muted">Validados</p>
              </div>
              <p className="text-2xl font-light text-emerald-600 mt-2 tabular-nums">{estadisticas.validados}</p>
            </div>
          </div>

          {/* ── Confidence Filter Pills ── */}
          <div className="flex flex-wrap items-center gap-3">
            {CONFIDENCE_PILLS.map(pill => {
              const isActive = confidenceFilter === pill.key;
              const count = pill.key === null
                ? confidenceCounts.todos
                : confidenceCounts[pill.key as keyof typeof confidenceCounts] || 0;
              return (
                <button
                  key={pill.label}
                  onClick={() => setConfidenceFilter(isActive && pill.key !== null ? null : pill.key)}
                  className={cn(
                    'rounded-huge px-5 py-2.5 text-sm font-light border transition-all flex items-center gap-2',
                    isActive
                      ? 'border-calm-text text-calm-text bg-white'
                      : 'bg-white border-calm-border text-calm-muted hover:border-calm-accent hover:text-calm-text'
                  )}
                >
                  {pill.key && (
                    <span
                      className={cn(
                        'w-2 h-2 rounded-full',
                        isActive
                          ? 'bg-calm-text/40'
                          : pill.key === 'alta' ? 'bg-emerald-500'
                          : pill.key === 'media' ? 'bg-amber-400'
                          : 'bg-red-500'
                      )}
                    />
                  )}
                  <span>{pill.label}</span>
                  <span className={cn(
                    'text-xs tabular-nums',
                    isActive ? 'text-calm-text/60' : 'text-calm-accent'
                  )}>
                    {count}
                  </span>
                  {isActive && pill.key !== null && (
                    <span
                      className="ml-0.5 hover:bg-calm-bg rounded-full p-0.5"
                      onClick={(e) => { e.stopPropagation(); setConfidenceFilter(null); }}
                    >
                      <Icon name="cancel" className="text-[14px]" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Filter Bar ── */}
          <FilterBar
            filters={filterConfig}
            values={filtros}
            onChange={handleFiltroChange}
            onClear={handleLimpiarFiltros}
          />

          {/* ── Bulk Actions Bar ── */}
          {selectedIds.size > 0 && (
            <div className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge p-6 flex flex-wrap items-center gap-3">
              <span className="text-sm font-light text-calm-text">
                {selectedIds.size} documento(s) seleccionado(s)
              </span>
              <button
                onClick={handleValidarTodos}
                className="bg-emerald-600 text-white rounded-huge px-5 py-2 text-sm font-light hover:bg-emerald-700 transition-colors flex items-center gap-2"
              >
                <Icon name="check_circle" className="text-[18px]" />
                Validar Todos
              </button>
              <button
                onClick={handleRechazarSeleccionados}
                className="border border-red-200 rounded-huge px-5 py-2 text-sm font-light text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors flex items-center gap-2"
              >
                <Icon name="cancel" className="text-[18px]" />
                Rechazar Seleccionados
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="border border-calm-border rounded-huge px-5 py-2 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-colors"
              >
                Deseleccionar
              </button>
            </div>
          )}

          {/* ── Documents Table ── */}
          <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge overflow-hidden">
            <div className="px-8 py-5 border-b border-fresh-sky-200/40 flex items-center justify-between">
              <h2 className="text-lg font-display font-light text-calm-text">
                Documentos IA
                <span className="ml-2 text-sm font-light text-calm-muted">
                  ({documentosFiltrados.length} documento{documentosFiltrados.length !== 1 ? 's' : ''})
                </span>
              </h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 px-3">
                    <Checkbox
                      checked={allTableSelected}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead className="px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium whitespace-nowrap">Documento</TableHead>
                  <TableHead className="px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium whitespace-nowrap">Cliente</TableHead>
                  <TableHead className="px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium whitespace-nowrap">Confianza</TableHead>
                  <TableHead className="px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium whitespace-nowrap">Estado</TableHead>
                  <TableHead className="px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium whitespace-nowrap">Nº Factura</TableHead>
                  <TableHead className="px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium whitespace-nowrap">Proveedor</TableHead>
                  <TableHead className="px-4 text-right text-[11px] uppercase tracking-widest text-calm-muted font-medium whitespace-nowrap">Importe</TableHead>
                  <TableHead className="px-4 text-[11px] uppercase tracking-widest text-calm-muted font-medium whitespace-nowrap">Fecha</TableHead>
                  <TableHead className="px-4 text-center text-[11px] uppercase tracking-widest text-calm-muted font-medium whitespace-nowrap">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documentosFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-2xl bg-fresh-sky-50 flex items-center justify-center text-fresh-sky-400 mb-4 mx-auto">
                          <Icon name="psychology" className="text-[48px]" />
                        </div>
                        <p className="text-sm font-light text-calm-text">No hay documentos para mostrar</p>
                        <p className="text-xs text-calm-accent mt-1">Ajusta los filtros o espera a que se procesen nuevos documentos</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  documentosFiltrados.map(doc => {
                    const cliente = findCliente(doc.clienteId);
                    const confianza = doc.datosIA?.nivelConfianza ?? 0;
                    const cc = getConfidenceColor(confianza);
                    const estadoInfo = getEstadoInfo(doc.estado);
                    const isSelected = selectedIds.has(doc.id);

                    return (
                      <TableRow
                        key={doc.id}
                        className={cn(isSelected && 'bg-calm-bg')}
                      >
                        <TableCell className="px-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelection(doc.id)}
                          />
                        </TableCell>
                        <TableCell className="px-4 font-light text-calm-text whitespace-nowrap max-w-[200px] truncate text-sm">
                          {doc.nombre}
                        </TableCell>
                        <TableCell className="px-4 text-sm font-light text-calm-muted whitespace-nowrap">
                          {cliente?.razonSocial || '-'}
                        </TableCell>
                        {/* Confidence with colored indicator */}
                        <TableCell className="px-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', cc.dot)} />
                            <Badge variant="outline" className={cn('rounded-full', cc.badge)}>
                              {confianza.toFixed(0)}%
                            </Badge>
                          </div>
                        </TableCell>
                        {/* Status with Badge */}
                        <TableCell className="px-4 whitespace-nowrap">
                          <Badge variant="outline" className={cn('rounded-full', estadoInfo.badge)}>
                            <span className={cn('w-2 h-2 rounded-full mr-1.5', estadoInfo.dot)} />
                            {estadoInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 text-sm font-light text-calm-text whitespace-nowrap">
                          {doc.datosIA?.numeroFactura || '-'}
                        </TableCell>
                        <TableCell className="px-4 text-sm font-light text-calm-muted whitespace-nowrap max-w-[180px] truncate">
                          {doc.datosIA?.proveedor || '-'}
                        </TableCell>
                        <TableCell className="px-4 text-right font-medium text-calm-text whitespace-nowrap font-mono text-sm">
                          {doc.datosIA?.importeTotal
                            ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(doc.datosIA.importeTotal)
                            : '-'}
                        </TableCell>
                        <TableCell className="px-4 text-sm font-light text-calm-muted whitespace-nowrap">
                          {doc.datosIA?.fecha
                            ? new Date(doc.datosIA.fecha).toLocaleDateString('es-ES')
                            : '-'}
                        </TableCell>
                        <TableCell className="px-4 text-center whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleVerDocumento(doc)}
                              className="p-2 rounded-xl text-calm-muted hover:text-calm-text hover:bg-calm-bg transition-all"
                              title="Ver documento"
                            >
                              <Icon name="visibility" className="text-[18px]" />
                            </button>
                            {doc.estado === 'procesado_ia' && (
                              <button
                                onClick={() => handleValidar(doc)}
                                className="p-2 rounded-xl text-calm-muted hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                                title="Validar"
                              >
                                <Icon name="check_circle" className="text-[18px]" />
                              </button>
                            )}
                            {doc.estado === 'validado' && (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-light px-2 py-1">
                                <Icon name="check_circle" className="text-[14px]" />
                                Validado
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* ── Expanded Detail Cards (below table for deeper info) ── */}
          {documentosFiltrados.some(d => d.datosIA?.asientoContable) && (
            <div className="space-y-6">
              <h2 className="text-lg font-display font-light text-calm-text">Asientos Contables Propuestos</h2>
              {documentosFiltrados.filter(d => d.datosIA?.asientoContable).map(doc => {
                const cliente = findCliente(doc.clienteId);
                const confianza = doc.datosIA?.nivelConfianza ?? 0;
                const cc = getConfidenceColor(confianza);
                return (
                  <div key={doc.id} className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8 hover:shadow-sm transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <h3 className="font-light text-calm-text">{doc.nombre}</h3>
                        <span className="text-sm font-light text-calm-muted">{cliente?.razonSocial}</span>
                      </div>
                      <Badge variant="outline" className={cn('rounded-full', cc.badge)}>
                        {confianza.toFixed(0)}% Confianza
                      </Badge>
                    </div>
                    <div className="bg-calm-bg rounded-huge p-6">
                      <div className="text-[10px] uppercase tracking-widest text-calm-accent mb-3 flex items-center gap-1.5">
                        <Icon name="auto_awesome" className="text-[14px]" />
                        Asiento Contable Propuesto (IA)
                      </div>
                      <div className="space-y-2">
                        {doc.datosIA?.asientoContable?.apuntes.map((apunte: any, idx: number) => (
                          <div key={idx} className="flex justify-between text-sm bg-white/80 rounded-xl px-4 py-2.5">
                            <span className="font-light text-calm-text">
                              {apunte.cuenta} - {apunte.concepto}
                            </span>
                            <span className={cn('font-medium', apunte.debe > 0 ? 'text-emerald-600' : 'text-red-600')}>
                              {apunte.debe > 0 ? `+${apunte.debe.toFixed(2)}€` : `-${apunte.haber.toFixed(2)}€`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Document Viewer Dialog ── */}
        <Dialog
          open={mostrarVisor && !!documentoSeleccionado}
          onOpenChange={(open) => {
            if (!open) {
              setMostrarVisor(false);
              setDocumentoSeleccionado(null);
            }
          }}
        >
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0 gap-0 rounded-huge border-calm-border">
            <DialogHeader className="p-8 pb-5 border-b border-calm-border">
              <DialogTitle className="text-2xl font-display font-light text-calm-text">{documentoSeleccionado?.nombre}</DialogTitle>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm font-light text-calm-muted">
                  {documentoSeleccionado && findCliente(documentoSeleccionado.clienteId)?.razonSocial}
                </span>
                {documentoSeleccionado?.datosIA && (() => {
                  const c = documentoSeleccionado.datosIA.nivelConfianza ?? 0;
                  const cc = getConfidenceColor(c);
                  return (
                    <Badge variant="outline" className={cn('rounded-full', cc.badge)}>
                      <span className={cn('w-2 h-2 rounded-full mr-1.5', cc.dot)} />
                      {c.toFixed(0)}% Confianza
                    </Badge>
                  );
                })()}
              </div>
            </DialogHeader>

            {documentoSeleccionado && (
              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <DocumentViewer documento={documentoSeleccionado} />
                  
                  <div className="space-y-6">
                    <div className="bg-calm-bg border border-calm-border rounded-huge p-6">
                      <h3 className="font-light text-calm-text mb-4 flex items-center gap-2">
                        <Icon name="psychology" className="text-[18px] text-calm-accent" />
                        <span className="text-[10px] uppercase tracking-widest text-calm-accent">Datos Extraídos (IA)</span>
                      </h3>
                      {documentoSeleccionado.datosIA && (
                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between">
                            <span className="font-light text-calm-muted">Confianza:</span>
                            <span className="font-medium text-calm-text">
                              {documentoSeleccionado.datosIA.nivelConfianza.toFixed(0)}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-light text-calm-muted">Número Factura:</span>
                            <span className="font-light text-calm-text">
                              {documentoSeleccionado.datosIA.numeroFactura || '-'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-light text-calm-muted">Proveedor:</span>
                            <span className="font-light text-calm-text">
                              {documentoSeleccionado.datosIA.proveedor || '-'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-light text-calm-muted">Importe Total:</span>
                            <span className="font-light text-calm-text">
                              {documentoSeleccionado.datosIA.importeTotal 
                                ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(documentoSeleccionado.datosIA.importeTotal)
                                : '-'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-light text-calm-muted">Fecha:</span>
                            <span className="font-light text-calm-text">
                              {documentoSeleccionado.datosIA.fecha 
                                ? new Date(documentoSeleccionado.datosIA.fecha).toLocaleDateString('es-ES')
                                : '-'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {documentoSeleccionado.datosIA?.asientoContable && (
                      <div className="bg-calm-bg border border-calm-border rounded-huge p-6">
                        <h3 className="font-light text-calm-text mb-4 flex items-center gap-2">
                          <Icon name="auto_awesome" className="text-[18px] text-calm-accent" />
                          <span className="text-[10px] uppercase tracking-widest text-calm-accent">Asiento Contable Propuesto</span>
                        </h3>
                        <div className="space-y-2">
                          {documentoSeleccionado.datosIA.asientoContable.apuntes.map((apunte: any, idx: number) => (
                            <div key={idx} className="bg-white rounded-xl p-4 border border-calm-border">
                              <div className="flex justify-between mb-1">
                                <span className="font-light text-calm-text">{apunte.cuenta}</span>
                                <span className={cn('font-medium', apunte.debe > 0 ? 'text-emerald-600' : 'text-red-600')}>
                                  {apunte.debe > 0 ? `+${apunte.debe.toFixed(2)}€` : `-${apunte.haber.toFixed(2)}€`}
                                </span>
                              </div>
                              <div className="text-sm font-light text-calm-muted">{apunte.concepto}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  {documentoSeleccionado.estado === 'procesado_ia' && (
                    <button
                      onClick={() => handleValidar(documentoSeleccionado)}
                      className="bg-emerald-600 text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-emerald-700 transition-colors flex items-center gap-2"
                    >
                      <Icon name="check_circle" className="text-[18px]" />
                      Validar y Enviar a ERP
                    </button>
                  )}
                  <button className="border border-amber-200 rounded-huge px-5 py-2.5 text-sm font-light text-amber-600 hover:bg-amber-50 transition-colors flex items-center gap-2">
                    <Icon name="edit" className="text-[18px]" />
                    Editar Asiento
                  </button>
                  <button className="border border-red-200 rounded-huge px-5 py-2.5 text-sm font-light text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors flex items-center gap-2">
                    <Icon name="warning" className="text-[18px]" />
                    Crear Incidencia
                  </button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </Layout>
    </>
  );
}
